import { levelsInChapter, LAST_LEVEL_ID } from './levels';
import type { FeatureFlag } from './featureFlags';
import type { LevelDef } from '../core/game';

/**
 * The Hub's gates (spec: docs/superpowers/specs/2026-09-02-the-hub-design.md).
 *
 * Both are DERIVED from progress the player already has, never stored: a
 * player who beat the campaign finale before The Hub shipped sees the button
 * the moment they load, and one who hit rank 10 last week finds the Potions
 * tab open. The `hub` / `potionshop` feature flags (`/give … unlock hub`)
 * are the dev override for either gate.
 */

/** Rank at which the Potion Shop opens (the silver boundary). */
export const POTION_SHOP_RANK = 10;

/**
 * Campaign level whose first win opens The Hub — Chapter 1's finale (Return
 * of the Golden Nugget), i.e. "complete the first chapter of the campaign".
 */
export const HUB_GATE_LEVEL_ID =
  levelsInChapter(1)[levelsInChapter(1).length - 1]?.id ?? LAST_LEVEL_ID;

export interface HubGateSource {
  stars(levelId: number): number;
  hasFlag(flag: FeatureFlag): boolean;
}

/** The Hub button is live once Chapter 1 has been completed (or flagged). */
export function hubUnlocked(p: HubGateSource): boolean {
  return p.stars(HUB_GATE_LEVEL_ID) >= 1 || p.hasFlag('hub');
}

/** The Potions tab is live from rank 10 (or flagged). */
export function potionShopUnlocked(rank: number, hasFlag: (f: FeatureFlag) => boolean): boolean {
  return rank >= POTION_SHOP_RANK || hasFlag('potionshop');
}

export const HUB_LOCK_HINT = 'Beat Chapter 1 to unlock';
export const POTION_LOCK_HINT = `Reach rank ${POTION_SHOP_RANK} to unlock`;

// ── The Market gate (spec Revision 8) ───────────────────────────────────────
// The Market shares the Potion Shop's boundary: rank 10, or the `potionshop`
// flag (`/give … unlock potionshop` opens both). Below it the MARKET tab still
// opens, but shows the Wise Hambi and MARKET_LOCK_TEXT instead of the rows.

/** Rank at which the Market opens (the same silver boundary as Potions). */
export const MARKET_RANK = POTION_SHOP_RANK;

/** The Market sells from rank 10 (or flagged). */
export function marketUnlocked(rank: number, hasFlag: (f: FeatureFlag) => boolean): boolean {
  return rank >= MARKET_RANK || hasFlag('potionshop');
}

/** What the locked Market says, over the Wise Hambi. */
export const MARKET_LOCK_TEXT = 'Get stronger in order to wield the power of the great.';
/** The art under MARKET_LOCK_TEXT — the evolved (tier 4) Wise Hambi. */
export const MARKET_LOCK_ART = 'tower-wise-4';

// ── The Diamond ─────────────────────────────────────────────────────────────
// One diamond exists: the prize for the first win of HTD Exhibition. Like the
// Variety Orb its three states live in one key (`hambi.diamond`).
export type DiamondState = '' | 'stored' | 'used';

/** Narrows an arbitrary stored string to a DiamondState (anything else is ''). */
export function asDiamondState(raw: string | null): DiamondState {
  return raw === 'stored' || raw === 'used' ? raw : '';
}

// ── The Moon Stone ──────────────────────────────────────────────────────────
// The Diamond's twin, and the chapter finale's prize: one exists, it is paid
// for the FIRST win of Through Space and Time, and its three states live in
// one key (`hambi.moonstone`). Spec:
// docs/superpowers/specs/2026-09-11-through-space-and-time-design.md §5.
export type MoonstoneState = '' | 'stored' | 'used';

/** Narrows an arbitrary stored string to a MoonstoneState (anything else is ''). */
export function asMoonstoneState(raw: string | null): MoonstoneState {
  return raw === 'stored' || raw === 'used' ? raw : '';
}

/**
 * Where the Evil Orb is (spec 2026-10-07): '' (never won, or taken by
 * `/remove`) or 'stored'. It does nothing yet, so it is never 'used'; it is
 * paid on the FIRST win of its level (stars-gated), so taking it away cannot
 * make a replay pay it again.
 */
export type EvilOrbState = '' | 'stored';

export function asEvilOrbState(raw: string | null): EvilOrbState {
  return raw === 'stored' ? 'stored' : '';
}

/**
 * The Token of Shroom (spec 2026-10-27): The Shroombellion's first-win prize.
 * The Evil Orb's rule exactly — '' or 'stored', paid on the FIRST win only.
 */
export type TokenOfShroomState = EvilOrbState;
export const asTokenOfShroomState = asEvilOrbState;

/**
 * Where the Light Orb is (spec 2026-10-13): '' (never had one), 'stored', or
 * 'taken' (removed by `/remove`). It does nothing yet, like the Evil Orb. It is
 * paid on ANY win of Heaven's Peak while it is '' — so a player who beat the
 * level before the orb existed still gets it on a replay — and 'taken' is what
 * stops a replay paying back an orb an admin removed.
 */
export type LightOrbState = '' | 'stored' | 'taken';

export function asLightOrbState(raw: string | null): LightOrbState {
  return raw === 'stored' || raw === 'taken' ? raw : '';
}

// ── Peaceful (spec Revision 6) ──────────────────────────────────────────────
// The Moon's PEACEFUL toggle: enemies deal no damage, every level pays
// PEACEFUL_COIN_PENALTY fewer coins, and progress is a SEPARATE track — its
// own cursor and stars under one key (`hambi.hubPeaceful`), so switching the
// toggle swaps the whole list between two independent saves of The Moon.
// Coins, the roster, rank and achievements stay shared: the penalty only
// means something if the coins land in the same wallet.

export const PEACEFUL_COIN_PENALTY = 30;

export interface PeacefulState {
  /** Whether the toggle is currently on (which track the picker shows). */
  on: boolean;
  /** Highest hub level number won on the Peaceful track (0 = none). */
  hub: number;
  /** Best stars per hub level id on the Peaceful track. */
  stars: Record<string, number>;
}

export const PEACEFUL_FRESH: PeacefulState = { on: false, hub: 0, stars: {} };

/**
 * Parse the stored Peaceful record, tolerating garbage (fresh). `maxHub`
 * clamps the cursor to the chapter's length.
 */
export function parsePeaceful(raw: string | null, maxHub: number): PeacefulState {
  if (!raw) return { ...PEACEFUL_FRESH, stars: {} };
  try {
    const v = JSON.parse(raw) as unknown;
    if (!v || typeof v !== 'object' || Array.isArray(v)) return { ...PEACEFUL_FRESH, stars: {} };
    const o = v as Record<string, unknown>;
    const hubRaw = typeof o.hub === 'number' && Number.isFinite(o.hub) ? Math.floor(o.hub) : 0;
    const stars: Record<string, number> = {};
    if (o.stars && typeof o.stars === 'object' && !Array.isArray(o.stars)) {
      for (const [k, s] of Object.entries(o.stars as Record<string, unknown>)) {
        if (typeof s === 'number' && Number.isFinite(s) && /^\d+$/.test(k)) {
          stars[k] = Math.max(0, Math.min(3, Math.floor(s)));
        }
      }
    }
    return { on: o.on === true, hub: Math.max(0, Math.min(hubRaw, maxHub)), stars };
  } catch {
    return { ...PEACEFUL_FRESH, stars: {} };
  }
}

/** A level's coin reward on the Peaceful track (never below 0). */
export function peacefulCoinReward(coinReward: number): number {
  return Math.max(0, coinReward - PEACEFUL_COIN_PENALTY);
}

/**
 * The runtime copy of a hub level a Peaceful run plays: the sim's `peaceful`
 * flag on, the coin reward already docked. Config levels are never mutated.
 */
export function peacefulLevel(level: LevelDef): LevelDef {
  return { ...level, peaceful: true, coinReward: peacefulCoinReward(level.coinReward) };
}
